import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import './CBCTGeomCalculator.css';

function CBCTGeomCalculator() {
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = 'CTViz | CBCT Volume Geometry Calculator';
    return () => { document.title = 'CTViz'; };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let volumeLocked = true;

    function syncVolumePeer(sourceId) {
      if (!volumeLocked) return;
      if (sourceId !== 'vw' && sourceId !== 'vh') return;
      const peerId = sourceId === 'vw' ? 'vh' : 'vw';
      const srcInp  = document.getElementById('inp-' + sourceId);
      const peerInp = document.getElementById('inp-' + peerId);
      const peerWrap = peerInp.closest('.drag-input-wrap');
      const peerFill = peerWrap.querySelector('.drag-fill');
      const peerMin  = parseFloat(peerWrap.dataset.min);
      const peerMax  = parseFloat(peerWrap.dataset.max);
      const peerDec  = parseInt(peerWrap.dataset.decimals || '0');
      peerInp.value  = parseFloat(srcInp.value).toFixed(peerDec);
      const v   = parseFloat(peerInp.value) || 0;
      const pct = Math.max(0, Math.min(100, ((v - peerMin) / (peerMax - peerMin)) * 100));
      peerFill.style.width = pct + '%';
    }

    // Declare update before drag setup — assigned below after helpers are defined
    let update;

    const cleanupFns = [];

    root.querySelectorAll('.drag-input-wrap').forEach(wrap => {
      const inp  = wrap.querySelector('input');
      const fill = wrap.querySelector('.drag-fill');
      const id   = wrap.dataset.id;
      const min  = parseFloat(wrap.dataset.min);
      const max  = parseFloat(wrap.dataset.max);
      const dec  = parseInt(wrap.dataset.decimals || '2');

      let dragging = false, pendingDrag = false, startX = 0, startVal = 0;

      function updateFill() {
        const v   = parseFloat(inp.value) || 0;
        const pct = Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
        fill.style.width = pct + '%';
      }

      function clamp(v) {
        return Math.max(min, Math.min(max, parseFloat(v.toFixed(dec))));
      }

      function onMouseDown(e) {
        if (document.activeElement === inp) return;
        e.preventDefault();
        pendingDrag = true;
        dragging    = false;
        startX      = e.clientX;
        startVal    = parseFloat(inp.value) || 0;
        wrap.classList.add('active');
        document.body.classList.add('cbct-dragging');
      }

      function onMouseMove(e) {
        if (!dragging && !pendingDrag) return;
        const dx = e.clientX - startX;
        if (pendingDrag && Math.abs(dx) > 3) {
          dragging    = true;
          pendingDrag = false;
        }
        if (!dragging) return;
        const sensitivity = (max - min) / 300;
        const newVal = clamp(startVal + dx * sensitivity);
        inp.value = newVal.toFixed(dec);
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      }

      function onMouseUp() {
        if (pendingDrag) {
          pendingDrag = false;
          wrap.classList.remove('active');
          document.body.classList.remove('cbct-dragging');
          inp.focus();
          inp.select();
        }
        if (dragging) {
          dragging = false;
          wrap.classList.remove('active');
          document.body.classList.remove('cbct-dragging');
        }
      }

      inp.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);

      inp.addEventListener('focus', () => { inp.style.cursor = 'text'; wrap.style.cursor = 'text'; });
      inp.addEventListener('blur',  () => {
        inp.style.cursor  = '';
        wrap.style.cursor = '';
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      });
      inp.addEventListener('input', () => {
        syncVolumePeer(id);
        updateFill();
        if (update) update();
      });
      inp.addEventListener('keydown', e => { if (e.key === 'Enter') inp.blur(); });

      updateFill();

      cleanupFns.push(() => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      });
    });

    function setVolumeLock(locked) {
      volumeLocked = locked;
      [document.getElementById('btn-lock-vwh'), document.getElementById('btn-lock-vwh-mid')].forEach(btn => {
        btn.classList.toggle('locked', locked);
        btn.textContent = locked ? '🔒' : '🔓';
      });
      if (locked) {
        syncVolumePeer('vw');
        if (update) update();
      }
    }

    document.getElementById('btn-lock-vwh').addEventListener('click',     () => setVolumeLock(!volumeLocked));
    document.getElementById('btn-lock-vwh-mid').addEventListener('click', () => setVolumeLock(!volumeLocked));

    function gv(id) { return parseFloat(document.getElementById('inp-' + id).value) || 0; }

    function getParams() {
      // Coordinate convention used by the calculator:
      // X = volume width, Z = volume depth in the rotation plane, Y = displayed vertical height.
      // The UI label "Z-height" maps to the vertical Y dimension intentionally, matching
      // the current cube/FOV interpretation while keeping source/detector orientation unchanged.
      const sod     = gv('sod');
      const sdd     = gv('sdd');
      const tiltDeg = gv('tilt');
      const tilt    = tiltDeg * Math.PI / 180;
      const dpw     = gv('dpw');
      const dph     = gv('dph');
      const pxsz    = gv('pxsz');
      const mag     = sdd / sod;
      const voxMM   = pxsz / mag;
      const vwPx    = gv('vw');
      const vhPx    = gv('vh');
      const vzPx    = gv('vz_size');
      const vwMM    = vwPx * voxMM;
      const vhMM    = vhPx * voxMM;
      const vzMM    = vzPx * voxMM;
      const cx      = gv('cx');
      const cy      = gv('cy');
      const cz      = gv('cz');
      const hOff    = gv('hoff');
      const vOff    = gv('voff');
      return { sod, sdd, tilt, tiltDeg, dpw, dph, pxsz, mag, voxMM,
               vwPx, vhPx, vzPx,
               vrMM: vwMM/2, vhMM: vhMM/2, vzMM: vzMM/2,
               cx, cy, cz,
               dwMM: dpw*pxsz, dhMM: dph*pxsz,
               hOff, vOff,
               hOff_mm: hOff * pxsz, vOff_mm: vOff * pxsz };
    }

    function compute(p) {
      const { sod, sdd, tilt, pxsz, mag, voxMM, vrMM, vhMM, vzMM,
              cx, cy, cz, dwMM, dhMM, hOff_mm, vOff_mm } = p;

      const fovR    = dwMM / 2 / mag;
      const fovH    = dhMM / 2 / mag;
      const tiltOff = sod * Math.tan(tilt);
      const coneAng = Math.atan(fovR / sod);

      const sinT = Math.sin(tilt), cosT = Math.cos(tilt);
      // Detector tilt is applied in the XZ source-detector plane. Detector vertical
      // coordinates remain world Y; this is why projection v uses ty below.
      const cxT  = cx*cosT + cz*sinT;
      const cyT  = cy;
      const czT  = -cx*sinT + cz*cosT;
      const srcZ = -sod;
      const detZ = sdd - sod;

      function projectPt(tx, ty, tz) {
        // Perspective projection from source plane z=-SOD to detector plane z=SDD-SOD
        // after the world point has been transformed into the tilted detector frame.
        const d = tz - srcZ;
        if (d <= 0) return null;
        const s = (detZ - srcZ) / d;
        return { u: tx*s, v: ty*s };
      }

      let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
      let allInside = true;
      const Nr = 36;

      function checkPt(ox, oy, oz) {
        // Transform world point into detector-aligned coordinates, then check whether
        // its detector footprint is inside the physical detector rectangle.
        const tx = ox*cosT + oz*sinT, ty = oy, tz = -ox*sinT + oz*cosT;
        const pt = projectPt(tx, ty, tz);
        if (!pt) { allInside = false; return; }
        if (Math.abs(pt.u - hOff_mm) > dwMM/2 || Math.abs(pt.v - vOff_mm) > dhMM/2) allInside = false;
        minU = Math.min(minU, pt.u); maxU = Math.max(maxU, pt.u);
        minV = Math.min(minV, pt.v); maxV = Math.max(maxV, pt.v);
      }

      // Sample the circular footprint in the rotation plane. The vertical samples use
      // vzMM because UI Z-height is currently interpreted as the cube's height.
      for (let i = 0; i < Nr; i++) {
        const a = (i/Nr) * 2 * Math.PI;
        for (const doy of [-vzMM, 0, vzMM]) {
          checkPt(cx + vrMM*Math.cos(a), cy + doy, cz + vhMM*Math.sin(a));
        }
      }

      // Check the rectangular box corners too; the inscribed circular samples do not
      // cover the square volume corners that can clip first.
      for (const dox of [-vrMM, vrMM]) {
        for (const doz of [-vhMM, vhMM]) {
          for (const doy of [-vzMM, 0, vzMM]) {
            checkPt(cx + dox, cy + doy, cz + doz);
          }
        }
      }

      const detectorFovOk = allInside;

      // Hard cylindrical FOV check for a full rotation. Width and Height define the
      // XZ footprint; Z-height is vertical and does not affect radial cylinder fit.
      const r_corner = Math.sqrt((Math.abs(cx) + vrMM) ** 2 + (Math.abs(cz) + vhMM) ** 2);
      const cylFovOk = r_corner <= fovR;
      if (!cylFovOk) allInside = false;

      let rotMinMarginPx = Infinity;
      const rotAngles = 180;
      const surfacePts = [];
      // Conservative detector guidance: sweep sampled surface points around the full
      // scan and record the tightest detector margin in pixels. This is not the hard
      // single-view footprint shown in the detector pane.
      for (const dox of [-vrMM, vrMM]) {
        for (const doz of [-vhMM, vhMM]) {
          for (const doy of [-vzMM, 0, vzMM]) {
            surfacePts.push([dox, doy, doz]);
          }
        }
      }
      for (let i = 0; i < Nr; i++) {
        const a = (i / Nr) * 2 * Math.PI;
        for (const doy of [-vzMM, 0, vzMM]) {
          surfacePts.push([vrMM * Math.cos(a), doy, vhMM * Math.sin(a)]);
        }
      }
      for (let ia = 0; ia < rotAngles; ia++) {
        const th = (ia / rotAngles) * 2 * Math.PI;
        const sinA = Math.sin(th), cosA = Math.cos(th);
        for (const [lx, ly, lz] of surfacePts) {
          const ox = cx + lx * cosA + lz * sinA;
          const oy = cy + ly;
          const oz = cz - lx * sinA + lz * cosA;
          const tx = ox*cosT + oz*sinT, ty = oy, tz = -ox*sinT + oz*cosT;
          const pt = projectPt(tx, ty, tz);
          if (!pt) {
            rotMinMarginPx = -Infinity;
            continue;
          }
          const mu = (dwMM/2 - Math.abs(pt.u - hOff_mm)) / pxsz;
          const mv = (dhMM/2 - Math.abs(pt.v - vOff_mm)) / pxsz;
          rotMinMarginPx = Math.min(rotMinMarginPx, mu, mv);
        }
      }

      const cylMarginMM = fovR - r_corner;
      const cylMarginVox = cylMarginMM / voxMM;
      const verticalConeAng = Math.atan(vzMM / sod);
      const verticalConeDeg = verticalConeAng * 180 / Math.PI;
      const conservativeLimits = {
        cylMarginVox: 10,
        detMarginPx: 20,
        coneDeg: 12,
      };
      // Conservative FDK gate. Hard geometry can pass while this fails; the failure
      // means the setup has little reconstruction margin, not that the object is
      // mathematically outside the detector/cylinder.
      const conservativeOk =
        allInside &&
        cylMarginVox >= conservativeLimits.cylMarginVox &&
        rotMinMarginPx >= conservativeLimits.detMarginPx &&
        verticalConeDeg <= conservativeLimits.coneDeg;
      const conservativeStatus = conservativeOk ? 'PASS' : 'FAIL';

      const projWpx = isFinite(maxU-minU) ? (maxU-minU)/pxsz : 0;
      const projHpx = isFinite(maxV-minV) ? (maxV-minV)/pxsz : 0;
      const projZpx = (vzMM*2 * mag) / pxsz;

      return {
        fovR, fovH, tiltOff, coneAng, allInside, detectorFovOk, cylFovOk, r_corner,
        cylMarginMM, cylMarginVox, rotMinMarginPx, verticalConeAng, verticalConeDeg,
        conservativeLimits, conservativeOk, conservativeStatus,
        projWpx, projHpx, projZpx,
        volWvox: vrMM*2/voxMM, volHvox: vhMM*2/voxMM, volZvox: vzMM*2/voxMM,
        mL: (hOff_mm + dwMM/2 - maxU) / pxsz,
        mR: (minU + dwMM/2 - hOff_mm) / pxsz,
        mT: (vOff_mm + dhMM/2 - maxV) / pxsz,
        mB: (minV + dhMM/2 - vOff_mm) / pxsz,
        minU, maxU, minV, maxV,
        cProj: projectPt(cxT, cyT, czT),
      };
    }

    function resizeCanvas(canvas) {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr  = window.devicePixelRatio || 1;
      if (canvas.width  !== Math.round(rect.width  * dpr) ||
          canvas.height !== Math.round(rect.height * dpr)) {
        canvas.width        = Math.round(rect.width  * dpr);
        canvas.height       = Math.round(rect.height * dpr);
        canvas.style.width  = rect.width  + 'px';
        canvas.style.height = rect.height + 'px';
      }
      return { ctx: canvas.getContext('2d'), w: rect.width, h: rect.height, dpr };
    }

    function drawDetector(p, r) {
      const canvas = document.getElementById('cv-det');
      const { ctx, w, h } = resizeCanvas(canvas);
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save(); ctx.scale(dpr, dpr);

      const cx = w/2, cy = h/2;
      const padX = 60, padY = 40;
      const sc   = Math.min((w-padX*2)/p.dpw, (h-padY*2)/p.dph);
      const dw   = p.dpw*sc, dh = p.dph*sc;
      const ox   = cx - dw/2 + p.hOff*sc;
      const oy   = cy - dh/2 + p.vOff*sc;

      ctx.fillStyle = 'rgba(55,138,221,0.05)';
      ctx.fillRect(ox, oy, dw, dh);
      ctx.strokeStyle = 'rgba(55,138,221,0.35)'; ctx.lineWidth = 1;
      ctx.strokeRect(ox, oy, dw, dh);

      ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 0.5;
      for (let i = 1; i < 8; i++) {
        const x = ox + dw*i/8, y2 = oy + dh*i/8;
        ctx.beginPath(); ctx.moveTo(x, oy); ctx.lineTo(x, oy+dh); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(ox, y2); ctx.lineTo(ox+dw, y2); ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(w, cy); ctx.stroke();

      const mmToC = (u, v) => [cx + (u/p.pxsz)*sc, cy + (v/p.pxsz)*sc];

      if (isFinite(r.minU) && isFinite(r.maxU)) {
        const [flx, fty] = mmToC(r.minU, r.minV);
        const [frx, fby] = mmToC(r.maxU, r.maxV);
        const fw = frx-flx, fh = fby-fty;

        ctx.fillStyle = r.allInside ? 'rgba(152,229,142,0.12)' : 'rgba(220,60,60,0.12)';
        ctx.fillRect(flx, fty, fw, fh);
        ctx.strokeStyle = r.allInside ? 'rgba(152,229,142,0.8)' : 'rgba(255,80,80,0.8)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(flx, fty, fw, fh);

        if (r.cProj) {
          const [pcx, pcy] = mmToC(r.cProj.u, r.cProj.v);
          ctx.fillStyle = r.allInside ? 'rgba(152,229,142,0.9)' : 'rgba(255,80,80,0.9)';
          ctx.beginPath(); ctx.arc(pcx, pcy, 3, 0, Math.PI*2); ctx.fill();
        }

        const midX = (flx+frx)/2;
        ctx.strokeStyle = r.allInside ? 'rgba(152,229,142,0.5)' : 'rgba(255,80,80,0.5)';
        ctx.lineWidth = 0.8; ctx.setLineDash([3,2]);
        ctx.beginPath(); ctx.moveTo(flx, fty-10); ctx.lineTo(frx, fty-10); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = r.allInside ? 'rgba(152,229,142,1)' : 'rgba(255,80,80,1)';
        ctx.font = `bold 10px 'Montserrat', sans-serif`; ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(r.projWpx)} px`, midX, fty-14);

        const midY = (fty+fby)/2;
        ctx.setLineDash([3,2]);
        ctx.beginPath(); ctx.moveTo(frx+10, fty); ctx.lineTo(frx+10, fby); ctx.stroke();
        ctx.setLineDash([]);
        ctx.save(); ctx.translate(frx+20, midY); ctx.rotate(Math.PI/2);
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(r.projHpx)} px`, 0, 0);
        ctx.restore();
      }

      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.font = `10px 'Montserrat', sans-serif`; ctx.textAlign = 'left';
      ctx.fillText(`${p.dpw} × ${p.dph} px`, ox+4, oy+dh-6);

      document.getElementById('sub-det').textContent =
        `${p.dpw}×${p.dph} px · ${p.pxsz.toFixed(3)} mm/px · offset ${p.hOff},${p.vOff} px`;

      ctx.restore();
    }

    // === 3D View Setup (runs once) ===
    const container3d = document.getElementById('cv-3d');
    const scene3d = new THREE.Scene();
    scene3d.background = new THREE.Color(0x040404);

    const cam3d = new THREE.PerspectiveCamera(45, 1, 0.001, 100000);
    const ren3d = new THREE.WebGLRenderer({ antialias: true });
    ren3d.setPixelRatio(window.devicePixelRatio || 1);
    ren3d.setSize(Math.max(container3d.clientWidth, 100), Math.max(container3d.clientHeight, 100));
    container3d.appendChild(ren3d.domElement);

    const controls3d = new OrbitControls(cam3d, ren3d.domElement);
    controls3d.enableDamping = true;
    controls3d.dampingFactor = 0.1;

    const group3d = new THREE.Group();
    scene3d.add(group3d);

    let animId;
    function animate3d() {
      animId = requestAnimationFrame(animate3d);
      controls3d.update();
      ren3d.render(scene3d, cam3d);
    }
    animate3d();

    function resize3d() {
      const w = container3d.clientWidth, h = container3d.clientHeight;
      if (!w || !h) return;
      cam3d.aspect = w / h;
      cam3d.updateProjectionMatrix();
      ren3d.setSize(w, h);
    }
    const ro3d = new ResizeObserver(resize3d);
    ro3d.observe(container3d);

    let cam3dDone = false;

    function draw3D(p, r) {
      while (group3d.children.length) {
        const o = group3d.children[0];
        group3d.remove(o);
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          if (Array.isArray(o.material)) o.material.forEach(m => m.dispose());
          else o.material.dispose();
        }
      }

      const sinT = Math.sin(p.tilt), cosT = Math.cos(p.tilt);

      // Keep source/detector geometry in the original scene orientation. Only the
      // volume dimensions are remapped so UI Z-height renders as vertical height.
      const srcPos  = new THREE.Vector3( sinT * p.sod, 0, -cosT * p.sod);
      const detBase = new THREE.Vector3(-sinT * (p.sdd - p.sod), 0, cosT * (p.sdd - p.sod));
      const hDir    = new THREE.Vector3(cosT, 0,  sinT);
      const vDir    = new THREE.Vector3(0, 1, 0);
      const detCtr  = detBase.clone()
        .addScaledVector(hDir, p.hOff_mm)
        .addScaledVector(vDir, p.vOff_mm);

      const hw = p.dwMM / 2, hh = p.dhMM / 2;
      const detTL = detCtr.clone().addScaledVector(hDir, -hw).addScaledVector(vDir,  hh);
      const detTR = detCtr.clone().addScaledVector(hDir,  hw).addScaledVector(vDir,  hh);
      const detBL = detCtr.clone().addScaledVector(hDir, -hw).addScaledVector(vDir, -hh);
      const detBR = detCtr.clone().addScaledVector(hDir,  hw).addScaledVector(vDir, -hh);

      function addLine(pts, color, opacity, dashed = false) {
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const ds = Math.max(p.sod * 0.06, 0.5);
        const mat = dashed
          ? new THREE.LineDashedMaterial({ color, opacity, transparent: true, dashSize: ds, gapSize: ds * 0.7 })
          : new THREE.LineBasicMaterial({ color, opacity, transparent: opacity < 1 });
        const line = new THREE.Line(geo, mat);
        if (dashed) line.computeLineDistances();
        group3d.add(line);
      }

      function addMesh(geo, color, opacity, wireframe = false) {
        const mat = new THREE.MeshBasicMaterial({
          color, opacity, transparent: opacity < 1,
          wireframe, side: THREE.DoubleSide,
          depthWrite: opacity > 0.5 && !wireframe,
        });
        const mesh = new THREE.Mesh(geo, mat);
        group3d.add(mesh);
        return mesh;
      }

      // Reference grid at Y=0
      const gridSpan = Math.pow(10, Math.ceil(Math.log10(p.sdd * 1.1)));
      group3d.add(new THREE.GridHelper(gridSpan, 10, 0x1c1c1c, 0x121212));

      // Optical axis (dashed green)
      addLine([srcPos.clone(), detBase.clone()], 0x98e58e, 0.2, true);

      // Source sphere + halo
      const ss = Math.max(p.sod * 0.04, 0.5);
      addMesh(new THREE.SphereGeometry(ss * 2.2, 10, 10), 0x98e58e, 0.12);
      addMesh(new THREE.SphereGeometry(ss,       10, 10), 0x98e58e, 1.0);

      // Cone beam: source → 4 detector corners
      [detTL, detTR, detBL, detBR].forEach(c =>
        addLine([srcPos.clone(), c.clone()], 0x378ADD, 0.22)
      );

      // Detector face fill
      const dv = new Float32Array([
        detTL.x, detTL.y, detTL.z,  detTR.x, detTR.y, detTR.z,  detBR.x, detBR.y, detBR.z,
        detTL.x, detTL.y, detTL.z,  detBR.x, detBR.y, detBR.z,  detBL.x, detBL.y, detBL.z,
      ]);
      const detFillGeo = new THREE.BufferGeometry();
      detFillGeo.setAttribute('position', new THREE.BufferAttribute(dv, 3));
      addMesh(detFillGeo, 0x378ADD, 0.07);
      // Detector outline + crosshair
      addLine([detTL, detTR, detBR, detBL, detTL], 0x378ADD, 0.8);
      addLine([detCtr.clone().addScaledVector(hDir, -hw * 0.06), detCtr.clone().addScaledVector(hDir, hw * 0.06)], 0x378ADD, 0.4);
      addLine([detCtr.clone().addScaledVector(vDir, -hh * 0.06), detCtr.clone().addScaledVector(vDir, hh * 0.06)], 0x378ADD, 0.4);

      // Isocenter
      addMesh(new THREE.SphereGeometry(Math.max(p.sod * 0.02, 0.3), 8, 8), 0xffffff, 0.4);

      // FOV cylinder remains vertical in world Y. Its radius represents the XZ
      // reconstruction footprint; its half-height represents detector vertical coverage.
      const fovCol3d = r.cylFovOk ? 0x98e58e : 0xff5050;
      addMesh(new THREE.CylinderGeometry(r.fovR, r.fovR, r.fovH * 2, 64, 1, true), fovCol3d, 0.07);
      for (const fy of [-r.fovH, r.fovH]) {
        const cap = addMesh(new THREE.CircleGeometry(r.fovR, 64), fovCol3d, 0.12);
        cap.rotation.x = -Math.PI / 2;
        cap.position.y = fy;
      }
      for (const fy of [-r.fovH, 0, r.fovH]) {
        const rPts = [];
        for (let i = 0; i <= 128; i++) {
          const a = (i / 128) * 2 * Math.PI;
          rPts.push(new THREE.Vector3(r.fovR * Math.cos(a), fy, r.fovR * Math.sin(a)));
        }
        addLine(rPts, fovCol3d, fy === 0 ? (r.cylFovOk ? 0.55 : 0.9) : 0.45);
      }

      // Volume box mapping: Width -> X, UI Z-height -> Y vertical, Height -> Z depth.
      const vc = r.allInside ? 0x98e58e : 0xff5050;
      const bw = p.vrMM * 2, bh = p.vzMM * 2, bd = p.vhMM * 2;
      const volFill = addMesh(new THREE.BoxGeometry(bw, bh, bd), vc, 0.07);
      volFill.position.set(p.cx, p.cy, p.cz);
      const volWire = addMesh(new THREE.BoxGeometry(bw, bh, bd), vc, 0.65, true);
      volWire.position.set(p.cx, p.cy, p.cz);

      // Auto-fit camera (first draw only)
      if (!cam3dDone) {
        cam3dDone = true;
        const bbox = new THREE.Box3();
        [srcPos, detTL, detTR, detBL, detBR,
          new THREE.Vector3(p.cx + p.vrMM, p.cy + p.vzMM, p.cz + p.vhMM),
          new THREE.Vector3(p.cx - p.vrMM, p.cy - p.vzMM, p.cz - p.vhMM),
        ].forEach(pt => bbox.expandByPoint(pt));
        const bsph = new THREE.Sphere();
        bbox.getBoundingSphere(bsph);
        const dist = bsph.radius * 3;
        cam3d.position.set(
          bsph.center.x + dist * 0.55,
          bsph.center.y + dist * 0.55,
          bsph.center.z + dist * 0.55
        );
        cam3d.lookAt(bsph.center);
        controls3d.target.copy(bsph.center);
        controls3d.update();
      }
    }

    // === FOV View Setup (fixed camera, auto-fits to object) ===
    const containerFov = document.getElementById('cv-fov');
    const sceneFov = new THREE.Scene();
    sceneFov.background = new THREE.Color(0x040404);

    const camFov = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.0001, 1000000);
    const renFov = new THREE.WebGLRenderer({ antialias: true });
    renFov.setPixelRatio(window.devicePixelRatio || 1);
    renFov.setSize(Math.max(containerFov.clientWidth, 100), Math.max(containerFov.clientHeight, 100));
    containerFov.appendChild(renFov.domElement);

    const groupFov = new THREE.Group();
    sceneFov.add(groupFov);

    const fovAxisGuide = root.querySelector('.fov-axis-guide');
    const fovAxisEls = {
      x: root.querySelector('.fov-axis-btn.x'),
      y: root.querySelector('.fov-axis-btn.y'),
      z: root.querySelector('.fov-axis-btn.z'),
    };
    const fovAxisLineEls = {
      x: root.querySelector('.fov-axis-line-x'),
      y: root.querySelector('.fov-axis-line-y'),
      z: root.querySelector('.fov-axis-line-z'),
    };

    let lastFovParams = null;
    let lastFovResult = null;

    const controlsFov = new OrbitControls(camFov, fovAxisGuide);
    controlsFov.enabled = false;
    controlsFov.enableDamping = false;
    controlsFov.enablePan = false;
    controlsFov.enableZoom = false;

    let animFovId;
    function animateFov() {
      animFovId = requestAnimationFrame(animateFov);
      controlsFov.update();
      updateFovAxisGuide();
      renFov.render(sceneFov, camFov);
    }
    animateFov();

    const roFov = new ResizeObserver(() => {
      const wf = containerFov.clientWidth, hf = containerFov.clientHeight;
      if (!wf || !hf) return;
      camFov.updateProjectionMatrix();
      renFov.setSize(wf, hf);
      if (lastFovParams && lastFovResult) fitFovCamera(lastFovParams, lastFovResult);
    });
    roFov.observe(containerFov);

    function detectorViewDir(p) {
      // Detector-facing FOV view: camera sits on the detector side looking back along
      // the central ray. This follows detector tilt but does not rotate the world axes.
      return new THREE.Vector3(-Math.sin(p.tilt), 0, Math.cos(p.tilt)).normalize();
    }

    function fitFovCamera(p, r) {
      // Refit the orthographic FOV view without giving users free camera control.
      // Axis buttons may snap views, but normal canvas dragging is intentionally inert.
      const viewR = Math.max(r.r_corner, r.fovR);
      const halfSpan = Math.max(viewR * 1.35, 0.001);
      const wf = Math.max(containerFov.clientWidth, 1);
      const hf = Math.max(containerFov.clientHeight, 1);
      const aspect = wf / hf;
      camFov.left = -halfSpan * Math.max(aspect, 1);
      camFov.right = halfSpan * Math.max(aspect, 1);
      camFov.top = halfSpan * Math.max(1 / aspect, 1);
      camFov.bottom = -halfSpan * Math.max(1 / aspect, 1);

      const target = new THREE.Vector3(p.cx, p.cy, p.cz);
      const dir = detectorViewDir(p);
      const camDist = Math.max(viewR * 10, 1);
      camFov.position.copy(target).addScaledVector(dir, camDist);
      camFov.up.set(0, 1, 0);
      camFov.lookAt(target);
      camFov.near = 0.0001;
      camFov.far = camDist * 2;
      camFov.updateProjectionMatrix();
      controlsFov.target.copy(target);
      controlsFov.update();
      updateFovAxisGuide();
    }

    function updateFovAxisGuide() {
      // Passive orientation widget: project world axes through the current FOV camera
      // so the guide reflects the fixed/snap view instead of controlling it directly.
      if (!fovAxisGuide || !fovAxisEls.x || !fovAxisLineEls.x) return;
      const centerX = fovAxisGuide.clientWidth / 2;
      const centerY = fovAxisGuide.clientHeight / 2;
      const radius = 25;
      const axes = {
        x: new THREE.Vector3(1, 0, 0),
        y: new THREE.Vector3(0, 1, 0),
        z: new THREE.Vector3(0, 0, 1),
      };

      Object.entries(axes).forEach(([axis, vec]) => {
        const viewVec = vec.clone().transformDirection(camFov.matrixWorldInverse);
        const x = centerX + viewVec.x * radius;
        const y = centerY - viewVec.y * radius;
        const len = Math.max(8, Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2));
        const angle = Math.atan2(y - centerY, x - centerX);
        const frontness = THREE.MathUtils.clamp((viewVec.z + 1) / 2, 0, 1);
        const opacity = 0.45 + frontness * 0.55;

        fovAxisEls[axis].style.left = `${x - 12}px`;
        fovAxisEls[axis].style.top = `${y - 12}px`;
        fovAxisEls[axis].style.opacity = opacity.toFixed(2);
        fovAxisEls[axis].style.zIndex = `${Math.round(frontness * 10) + 2}`;

        fovAxisLineEls[axis].style.width = `${len}px`;
        fovAxisLineEls[axis].style.transform = `rotate(${angle}rad)`;
        fovAxisLineEls[axis].style.opacity = (opacity * 0.6).toFixed(2);
      });
    }

    function setFovGuideActive(axis) {
      root.querySelectorAll('.fov-axis-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.axis === axis);
      });
    }

    const fovAxisSign = { x: 1, y: 1, z: 1 };
    let fovActiveAxis = 'iso';

    function snapFovCamera(axis, sign) {
      // Axis snaps are deterministic camera presets for inspection. Re-clicking the
      // same axis flips to the opposite side; the iso button returns to detector-facing.
      if (!lastFovParams || !lastFovResult) return;
      const p = lastFovParams, r = lastFovResult;
      const target = new THREE.Vector3(p.cx, p.cy, p.cz);
      const viewR = Math.max(r.r_corner, r.fovR);
      const halfSpan = Math.max(viewR * 1.35, 0.001);
      const camDist = Math.max(viewR * 10, 1);
      const wf = Math.max(containerFov.clientWidth, 1);
      const hf = Math.max(containerFov.clientHeight, 1);
      const aspect = wf / hf;
      camFov.left   = -halfSpan * Math.max(aspect, 1);
      camFov.right  =  halfSpan * Math.max(aspect, 1);
      camFov.top    =  halfSpan * Math.max(1 / aspect, 1);
      camFov.bottom = -halfSpan * Math.max(1 / aspect, 1);
      camFov.near   = 0.0001;
      camFov.far    = camDist * 2;
      let pos, up;
      if (axis === 'x') {
        pos = target.clone().add(new THREE.Vector3(sign * camDist, 0, 0));
        up  = new THREE.Vector3(0, 1, 0);
      } else if (axis === 'y') {
        pos = target.clone().add(new THREE.Vector3(0, sign * camDist, 0));
        up  = sign > 0 ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0, 0, 1);
      } else {
        pos = target.clone().add(new THREE.Vector3(0, 0, sign * camDist));
        up  = new THREE.Vector3(0, 1, 0);
      }
      camFov.position.copy(pos);
      camFov.up.copy(up);
      camFov.lookAt(target);
      camFov.updateProjectionMatrix();
      controlsFov.target.copy(target);
      controlsFov.update();
      updateFovAxisGuide();
    }

    root.querySelectorAll('.fov-axis-btn').forEach(btn => {
      const stopPointer = e => e.stopPropagation();
      const onClick = e => {
        e.stopPropagation();
        const axis = btn.dataset.axis;
        if (axis === 'iso') {
          fovActiveAxis = 'iso';
          setFovGuideActive('iso');
          if (lastFovParams && lastFovResult) fitFovCamera(lastFovParams, lastFovResult);
          return;
        }
        if (fovActiveAxis === axis) {
          fovAxisSign[axis] *= -1;
        } else {
          fovAxisSign[axis] = 1;
          fovActiveAxis = axis;
        }
        setFovGuideActive(axis);
        snapFovCamera(axis, fovAxisSign[axis]);
      };
      btn.addEventListener('pointerdown', stopPointer);
      btn.addEventListener('click', onClick);
      cleanupFns.push(() => {
        btn.removeEventListener('pointerdown', stopPointer);
        btn.removeEventListener('click', onClick);
      });
    });
    setFovGuideActive('iso');

    function drawFOV(p, r) {
      lastFovParams = p;
      lastFovResult = r;

      while (groupFov.children.length) {
        const o = groupFov.children[0];
        groupFov.remove(o);
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          if (Array.isArray(o.material)) o.material.forEach(m => m.dispose());
          else o.material.dispose();
        }
      }

      const sinT = Math.sin(p.tilt), cosT = Math.cos(p.tilt);
      const srcPos  = new THREE.Vector3( sinT * p.sod, 0, -cosT * p.sod);
      const detBase = new THREE.Vector3(-sinT * (p.sdd - p.sod), 0, cosT * (p.sdd - p.sod));
      const hDir = new THREE.Vector3(cosT, 0, sinT);
      const vDir = new THREE.Vector3(0, 1, 0);
      const detCtr = detBase.clone()
        .addScaledVector(hDir, p.hOff_mm)
        .addScaledVector(vDir, p.vOff_mm);
      const hw = p.dwMM / 2;
      const corners = [
        detCtr.clone().addScaledVector(hDir, -hw).addScaledVector(vDir,  p.dhMM / 2),
        detCtr.clone().addScaledVector(hDir,  hw).addScaledVector(vDir,  p.dhMM / 2),
        detCtr.clone().addScaledVector(hDir, -hw).addScaledVector(vDir, -p.dhMM / 2),
        detCtr.clone().addScaledVector(hDir,  hw).addScaledVector(vDir, -p.dhMM / 2),
      ];

      function addFovLine(pts, color, opacity) {
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const mat = new THREE.LineBasicMaterial({ color, opacity, transparent: opacity < 1 });
        groupFov.add(new THREE.Line(geo, mat));
      }
      function addFovMesh(geo, color, opacity, wireframe = false) {
        const mat = new THREE.MeshBasicMaterial({
          color, opacity, transparent: opacity < 1, wireframe,
          side: THREE.DoubleSide, depthWrite: opacity > 0.5 && !wireframe,
        });
        const mesh = new THREE.Mesh(geo, mat);
        groupFov.add(mesh);
        return mesh;
      }

      // Subtle grid
      const gridSpan = Math.pow(10, Math.ceil(Math.log10(Math.max(r.r_corner, r.fovR) * 8)));
      groupFov.add(new THREE.GridHelper(gridSpan, 10, 0x0d0d0d, 0x0a0a0a));

      // Cone rays: source → detector corners, extended far past detector
      const extend = p.sdd * 4;
      corners.forEach(c => {
        const dir = c.clone().sub(srcPos).normalize();
        addFovLine([
          srcPos.clone().sub(dir.clone().multiplyScalar(p.sod * 0.15)),
          srcPos.clone().add(dir.clone().multiplyScalar(extend)),
        ], 0x2a7acc, 0.35);
      });

      // FOV cylinder matches the hard geometry convention: radius in XZ, height in Y.
      const fovCol = r.cylFovOk ? 0x98e58e : 0xff5050;
      addFovMesh(new THREE.CylinderGeometry(r.fovR, r.fovR, r.fovH * 2, 64, 1, true), fovCol, 0.14);
      for (const fy of [-r.fovH, r.fovH]) {
        const cap = addFovMesh(new THREE.CircleGeometry(r.fovR, 64), fovCol, 0.12);
        cap.rotation.x = -Math.PI / 2;
        cap.position.y = fy;
      }
      for (const fy of [-r.fovH, 0, r.fovH]) {
        const rPts = [];
        for (let i = 0; i <= 128; i++) {
          const a = (i / 128) * 2 * Math.PI;
          rPts.push(new THREE.Vector3(r.fovR * Math.cos(a), fy, r.fovR * Math.sin(a)));
        }
        addFovLine(rPts, fovCol, fy === 0 ? (r.cylFovOk ? 0.85 : 1.0) : 0.35);
      }

      // Same volume mapping as draw3D: Width -> X, UI Z-height -> Y, Height -> Z.
      const vc = r.allInside ? 0x98e58e : 0xff5050;
      const bw = p.vrMM * 2, bh = p.vzMM * 2, bd = p.vhMM * 2;
      const vFill = addFovMesh(new THREE.BoxGeometry(bw, bh, bd), vc, 0.09);
      vFill.position.set(p.cx, p.cy, p.cz);
      const vWire = addFovMesh(new THREE.BoxGeometry(bw, bh, bd), vc, 0.8, true);
      vWire.position.set(p.cx, p.cy, p.cz);

      if (!r.cylFovOk) {
        // Mark the XZ corners that violate the cylindrical radius. These markers are
        // only about cylinder fit, not detector projection.
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            const x = p.cx + sx * p.vrMM;
            const z = p.cz + sz * p.vhMM;
            if (Math.sqrt(x * x + z * z) <= r.fovR) continue;
            const marker = addFovMesh(new THREE.SphereGeometry(Math.max(r.fovR * 0.025, 0.001), 10, 10), 0xff5050, 1);
            marker.position.set(x, p.cy, z);
            addFovLine([new THREE.Vector3(0, p.cy, 0), new THREE.Vector3(x, p.cy, z)], 0xff5050, 0.9);
          }
        }
      }

      // Isocenter
      addFovMesh(new THREE.SphereGeometry(Math.max(r.fovR * 0.06, 0.001), 8, 8), 0xffffff, 0.5);

      if (fovActiveAxis === 'iso') { fitFovCamera(p, r); }
      else { snapFovCamera(fovActiveAxis, fovAxisSign[fovActiveAxis]); }
    }

    update = function() {
      const p = getParams();
      const r = compute(p);

      // Header intentionally compresses hard geometry and conservative FDK checks
      // into a single within/out-of-FOV state; the detail cards show which limit failed.
      document.getElementById('hr-projw').textContent = Math.round(r.projWpx).toLocaleString();
      document.getElementById('hr-projh').textContent = Math.round(r.projHpx).toLocaleString();
      document.getElementById('hr-projz').textContent = Math.round(r.projZpx).toLocaleString();
      document.getElementById('hr-voxw').textContent  = Math.round(r.volWvox).toLocaleString();
      document.getElementById('hr-voxh').textContent  = Math.round(r.volHvox).toLocaleString();
      document.getElementById('hr-voxz').textContent  = Math.round(r.volZvox).toLocaleString();

      ['hr-projw','hr-projh','hr-projz'].forEach(id => {
        document.getElementById(id).className = 'hm-val ' + (r.detectorFovOk ? '' : 'err');
      });

      document.getElementById('r-mag').textContent     = p.mag.toFixed(2) + '×';
      document.getElementById('r-vox').textContent     = p.voxMM.toFixed(5) + ' mm';
      document.getElementById('r-det-mm').textContent  = `${p.dwMM.toFixed(1)} × ${p.dhMM.toFixed(1)} mm`;
      document.getElementById('r-fovr').textContent    = r.fovR.toFixed(4) + ' mm';
      document.getElementById('r-fovh').textContent    = r.fovH.toFixed(4) + ' mm';
      document.getElementById('r-toff').textContent    = r.tiltOff.toFixed(4) + ' mm';
      document.getElementById('r-cone').textContent    = (r.coneAng * 180 / Math.PI).toFixed(4) + '°';
      const rcEl = document.getElementById('r-corner');
      rcEl.textContent  = r.r_corner.toFixed(4) + ' mm';
      rcEl.className    = 'val ' + (r.cylFovOk ? '' : 'warn');
      const cmEl = document.getElementById('r-cyl-margin');
      cmEl.textContent = `${r.cylMarginVox.toFixed(1)} vox`;
      cmEl.className = 'val ' + (r.cylMarginVox >= r.conservativeLimits.cylMarginVox ? '' : 'warn');
      const admEl = document.getElementById('r-all-det-margin');
      admEl.textContent = `${Math.round(r.rotMinMarginPx).toLocaleString()} px`;
      admEl.className = 'val ' + (r.rotMinMarginPx >= r.conservativeLimits.detMarginPx ? '' : 'warn');
      const vcEl = document.getElementById('r-vcone');
      vcEl.textContent = r.verticalConeDeg.toFixed(2) + '°';
      vcEl.className = 'val ' + (r.verticalConeDeg <= r.conservativeLimits.coneDeg ? '' : 'warn');
      const conservativeStatusEl = document.getElementById('r-conservative-status');
      conservativeStatusEl.textContent = r.conservativeStatus;
      conservativeStatusEl.className = 'val ' + (r.conservativeOk ? 'accent' : 'warn');

      const mVals = [r.mL, r.mR, r.mT, r.mB];
      ['ml','mr','mt','mb'].forEach((id, i) => {
        const el = document.getElementById('r-' + id);
        el.textContent = Math.round(mVals[i]).toLocaleString() + ' px';
        el.className   = 'val ' + (mVals[i] < 0 ? 'warn' : '');
      });

      const badge = document.getElementById('hdr-badge');
      badge.textContent = r.conservativeOk ? 'WITHIN FOV' : 'OUT OF FOV';
      badge.className   = 'badge-status ' + (r.conservativeOk ? 'ok' : 'err');
      document.getElementById('sub-fov').textContent =
        `${r.cylFovOk ? 'cylinder OK' : 'outside cylinder'} · ${r.detectorFovOk ? 'detector OK' : 'detector footprint fails'} · conservative ${r.conservativeStatus.toLowerCase()}`;

      drawDetector(p, r);
      draw3D(p, r);
      drawFOV(p, r);
    };

    const ro = new ResizeObserver(() => update());
    ro.observe(document.getElementById('cv-det').parentElement);

    update();

    return () => {
      cancelAnimationFrame(animId);
      cancelAnimationFrame(animFovId);
      controls3d.dispose();
      controlsFov.dispose();
      ro3d.disconnect();
      roFov.disconnect();
      [
        { group: group3d, ren: ren3d, container: container3d },
        { group: groupFov, ren: renFov, container: containerFov },
      ].forEach(({ group, ren, container }) => {
        while (group.children.length) {
          const o = group.children[0];
          group.remove(o);
          if (o.geometry) o.geometry.dispose();
          if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach(m => m.dispose());
            else o.material.dispose();
          }
        }
        ren.dispose();
        if (ren.domElement.parentNode === container) container.removeChild(ren.domElement);
      });
      ro.disconnect();
      cleanupFns.forEach(fn => fn());
    };
  }, []);

  return (
    <div className="cbct-page" ref={rootRef}>

      <header>
        <Link to="/" className="header-home-link">
          <div className="logo-dot"></div>
          <h1>CBCT · Volume Geometry Calculator</h1>
        </Link>
        <div className="sep"></div>
        <span className="badge-status ok" id="hdr-badge">WITHIN FOV</span>
      </header>

      <aside className="panel-left">

        <div>
          <div className="section-label">Geometry</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">SOD <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="sod" data-min="0.1" data-max="100" data-step="0.1" data-decimals="1">
                <div className="drag-fill" id="fill-sod"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-sod" defaultValue="4.9" min="0.1" max="100" step="0.1" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">SDD <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="sdd" data-min="1" data-max="2000" data-step="1" data-decimals="0">
                <div className="drag-fill" id="fill-sdd"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-sdd" defaultValue="490" min="1" max="2000" step="1" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Tilt X <span className="unit">°</span></div>
              <div className="drag-input-wrap" data-id="tilt" data-min="-89" data-max="89" data-step="0.5" data-decimals="1">
                <div className="drag-fill" id="fill-tilt"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-tilt" defaultValue="51" min="-89" max="89" step="0.5" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Detector</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">Width × Height <span className="unit">px</span></div>
              <div className="field-row">
                <div className="drag-input-wrap" data-id="dpw" data-min="1" data-max="10000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-dpw"></div>
                  <span className="drag-label-inner">W</span>
                  <input type="number" id="inp-dpw" defaultValue="2401" min="1" max="10000" step="1" />
                </div>
                <div className="drag-input-wrap" data-id="dph" data-min="1" data-max="10000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-dph"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-dph" defaultValue="2803" min="1" max="10000" step="1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Pixel size <span className="unit">mm/px</span></div>
              <div className="drag-input-wrap" data-id="pxsz" data-min="0.001" data-max="2" data-step="0.001" data-decimals="3">
                <div className="drag-fill" id="fill-pxsz"></div>
                <span className="drag-label-inner">drag ←→</span>
                <input type="number" id="inp-pxsz" defaultValue="0.1" min="0.001" max="2" step="0.001" />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Detector Offset <span className="unit">px</span></div>
              <div className="field-row">
                <div className="drag-input-wrap" data-id="hoff" data-min="-5000" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-hoff"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-hoff" defaultValue="0" min="-5000" max="5000" step="1" />
                </div>
                <div className="drag-input-wrap" data-id="voff" data-min="-5000" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-voff"></div>
                  <span className="drag-label-inner">V</span>
                  <input type="number" id="inp-voff" defaultValue="0" min="-5000" max="5000" step="1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume Size <span style={{fontWeight:400,textTransform:'none',letterSpacing:0}}>(px)</span></div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field">
              <div className="field-label">
                Width × Height <span className="unit">px</span>
                <button className="lock-btn locked" id="btn-lock-vwh" title="Lock W = H">🔒</button>
              </div>
              <div className="field-row-lock">
                <div className="drag-input-wrap" data-id="vw" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-vw"></div>
                  <span className="drag-label-inner">W</span>
                  <input type="number" id="inp-vw" defaultValue="200" min="1" max="5000" step="1" />
                </div>
                <button className="lock-btn locked" id="btn-lock-vwh-mid" tabIndex="-1" aria-hidden="true">🔒</button>
                <div className="drag-input-wrap" data-id="vh" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                  <div className="drag-fill" id="fill-vh"></div>
                  <span className="drag-label-inner">H</span>
                  <input type="number" id="inp-vh" defaultValue="200" min="1" max="5000" step="1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Z-height <span className="unit">px</span></div>
              <div className="drag-input-wrap" data-id="vz_size" data-min="1" data-max="5000" data-step="1" data-decimals="0">
                <div className="drag-fill" id="fill-vz_size"></div>
                <span className="drag-label-inner">Z</span>
                <input type="number" id="inp-vz_size" defaultValue="200" min="1" max="5000" step="1" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume Centre <span style={{fontWeight:400,textTransform:'none',letterSpacing:0}}>(mm)</span></div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <div className="field-row">
              <div className="field">
                <div className="field-label">X <span className="unit">mm</span></div>
                <div className="drag-input-wrap" data-id="cx" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                  <div className="drag-fill" id="fill-cx"></div>
                  <input type="number" id="inp-cx" defaultValue="0" min="-200" max="200" step="0.1" />
                </div>
              </div>
              <div className="field">
                <div className="field-label">Y <span className="unit">mm</span></div>
                <div className="drag-input-wrap" data-id="cy" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                  <div className="drag-fill" id="fill-cy"></div>
                  <input type="number" id="inp-cy" defaultValue="0" min="-200" max="200" step="0.1" />
                </div>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Z <span className="unit">mm</span></div>
              <div className="drag-input-wrap" data-id="cz" data-min="-200" data-max="200" data-step="0.1" data-decimals="1">
                <div className="drag-fill" id="fill-cz"></div>
                <input type="number" id="inp-cz" defaultValue="0" min="-200" max="200" step="0.1" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-label">Volume on Detector</div>
          <div className="hero-results">
            <div className="hero-metric">
              <div className="hm-label">Proj. W</div>
              <div className="hm-val" id="hr-projw">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Proj. H</div>
              <div className="hm-val" id="hr-projh">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Proj. Z</div>
              <div className="hm-val" id="hr-projz">—</div>
              <div className="hm-sub">pixels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. W</div>
              <div className="hm-val accent" id="hr-voxw">—</div>
              <div className="hm-sub">voxels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. H</div>
              <div className="hm-val accent" id="hr-voxh">—</div>
              <div className="hm-sub">voxels</div>
            </div>
            <div className="hero-metric">
              <div className="hm-label">Vox. Z</div>
              <div className="hm-val accent" id="hr-voxz">—</div>
              <div className="hm-sub">voxels</div>
            </div>
          </div>
        </div>

        <div className="results-card">
          <div className="section-label">System</div>
          <div className="result-row"><span className="key">Magnification</span><span className="val accent" id="r-mag">—</span></div>
          <div className="result-row"><span className="key">Voxel size (iso)</span><span className="val" id="r-vox">—</span></div>
          <div className="result-row"><span className="key">Det. size (mm)</span><span className="val" id="r-det-mm">—</span></div>
          <div className="result-row"><span className="key">FOV radius (mm)</span><span className="val" id="r-fovr">—</span></div>
          <div className="result-row"><span className="key">FOV half-height</span><span className="val" id="r-fovh">—</span></div>
          <div className="result-row"><span className="key">Tilt offset</span><span className="val" id="r-toff">—</span></div>
          <div className="result-row"><span className="key">Cone half-angle</span><span className="val" id="r-cone">—</span></div>
          <div className="result-row"><span className="key">Max corner radius</span><span className="val" id="r-corner">—</span></div>
        </div>

        <div className="results-card">
          <div className="section-label">Conservative FDK</div>
          <div className="result-row"><span className="key">Status</span><span className="val" id="r-conservative-status">—</span></div>
          <div className="result-row"><span className="key">Cylinder margin</span><span className="val" id="r-cyl-margin">—</span></div>
          <div className="result-row"><span className="key">All-angle det. margin</span><span className="val" id="r-all-det-margin">—</span></div>
          <div className="result-row"><span className="key">Vertical cone angle</span><span className="val" id="r-vcone">—</span></div>
        </div>

        <div className="results-card">
          <div className="section-label">FOV Margins (px)</div>
          <div className="result-row"><span className="key">Left</span><span className="val" id="r-ml">—</span></div>
          <div className="result-row"><span className="key">Right</span><span className="val" id="r-mr">—</span></div>
          <div className="result-row"><span className="key">Top</span><span className="val" id="r-mt">—</span></div>
          <div className="result-row"><span className="key">Bottom</span><span className="val" id="r-mb">—</span></div>
        </div>

      </aside>

      <main className="panel-right">
        <div className="canvas-pane">
          <div className="pane-label">FOV View — Detector-Facing</div>
          <div className="pane-sublabel" id="sub-fov">—</div>
          <div id="cv-fov"></div>
          <div className="fov-axis-guide" aria-label="FOV view orientation">
            <div className="fov-axis-line fov-axis-line-x"></div>
            <div className="fov-axis-line fov-axis-line-z"></div>
            <div className="fov-axis-line fov-axis-line-y"></div>
            <button className="fov-axis-btn z" type="button" data-axis="z" title="Reset view">Z</button>
            <button className="fov-axis-btn y" type="button" data-axis="y" title="Reset view">Y</button>
            <button className="fov-axis-btn x" type="button" data-axis="x" title="Reset view">X</button>
            <button className="fov-axis-btn iso" type="button" data-axis="iso" title="Reset to detector-facing view">⟳</button>
          </div>
        </div>
        <div className="canvas-pane">
          <div className="pane-label">Detector View — Projection Footprint</div>
          <div className="pane-sublabel" id="sub-det">—</div>
          <canvas id="cv-det"></canvas>
        </div>
        <div className="canvas-pane" style={{gridColumn: '1 / -1'}}>
          <div className="pane-label">3D View — Cone Beam Geometry</div>
          <div className="pane-sublabel">drag · scroll · right-click</div>
          <div id="cv-3d"></div>
        </div>
      </main>

    </div>
  );
}

export default CBCTGeomCalculator;
